import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import API from '../api';

export const PUBLIC_PATHS = ['/', '/login', '/register', '/verify', '/reset-password', '/compte-ferme'];

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const isPublicPath = PUBLIC_PATHS.includes(location.pathname);

    if (isPublicPath) {
      setAuthChecked(true);
      return () => {
        cancelled = true;
      };
    }

    if (user) {
      setAuthChecked(true);
      return () => {
        cancelled = true;
      };
    }

    setAuthChecked(false);

    API.get('/user/')
      .then((res) => {
        if (!cancelled) {
          setUser(res.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUser(null);
          navigate('/login', { replace: true });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAuthChecked(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate, user]);

  const logout = useCallback(async () => {
    try {
      await API.post('/logout/');
    } catch (err) {
      // The session may already be gone; the UI should still return to login.
    }
    setUser(null);
    navigate('/login');
  }, [navigate]);

  const value = useMemo(() => ({
    authChecked,
    logout,
    setUser,
    user,
  }), [authChecked, logout, user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}

export function RequireAuth({ children }) {
  const { authChecked, user } = useAuth();
  const location = useLocation();

  if (!authChecked) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
}
